import {
  DEFAULT_GIFT_CARD_MINIMUM_DAYS_HELD,
  MarketingGiftCardBalanceTriggerStep,
  TriggerStep,
} from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { useTranslation } from "@redotech/redo-i18n";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useState } from "react";
import { PartialWithRequiredType } from "../../steps";

const MAX_DAYS_HELD = 365;

function parseDaysHeld(textValue: string): number | null {
  const parsed = parseInt(textValue);
  if (isNaN(parsed) || parsed < 0 || parsed > MAX_DAYS_HELD) {
    return null;
  }
  return parsed;
}

export const GiftCardBalanceFieldSelector = memo(
  function GiftCardBalanceFieldSelector({
    value,
    valueChange,
  }: {
    value: PartialWithRequiredType<MarketingGiftCardBalanceTriggerStep>;
    valueChange: (value: PartialWithRequiredType<TriggerStep>) => void;
  }) {
    const { t } = useTranslation();
    const [textValue, setTextValue_] = useState<string>(
      "" +
        (value.triggerSpecificFields?.minimumDaysHeld ??
          DEFAULT_GIFT_CARD_MINIMUM_DAYS_HELD),
    );

    const setTextValue = useCallback(
      (nextTextValue: string) => {
        setTextValue_(nextTextValue);
        const minimumDaysHeld = parseDaysHeld(nextTextValue);
        if (minimumDaysHeld === null) {
          return;
        }
        valueChange({ ...value, triggerSpecificFields: { minimumDaysHeld } });
      },
      [value, valueChange],
    );

    return (
      <Flex dir="column" gap="xl">
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          {t("Days since they got the balance")}
        </Text>
        <Flex dir="column" gap="xs">
          <RedoTextInput
            inputTypeHint="number"
            min={0}
            onBlur={() => {
              if (parseDaysHeld(textValue) === null) {
                setTextValue("" + DEFAULT_GIFT_CARD_MINIMUM_DAYS_HELD);
              }
            }}
            setValue={setTextValue}
            value={textValue}
          />
          <Text fontSize="xxs" textColor="secondary">
            {t(
              "Wait this many days after a customer redeems a gift card, or is issued refund credit, before contacting them about the leftover balance. Counted in your store's timezone.",
            )}
          </Text>
        </Flex>
      </Flex>
    );
  },
);
